import { CameraAlt } from "@material-ui/icons";
import AddIcon from '@material-ui/icons/Add';
import React from "react";
import { useTranslation } from "react-i18next";

import { SelectOptions } from "../../../assets/data/options";
import { CostCalculationPercentage } from "../../../backend/generated/cost-calculation-percentage-model";
import { Property } from "../../../backend/generated/property-model";
import { maxScoreInvestmentClass } from "../../../backend/types/user-scoring";
import { CostCalculationPercentageType, PropertyType } from "../../../backend/types/utils";
import i18n from "../../../services/i18n";
import { CreateSaleManageable, SaleInputTypeUnion, SaleTypeIntersection } from "../../../store/sale/types";
import { SubformProps } from "../../SubformProps";
import { AcceptedFileType } from "../elements/FileUpload";
import { InputFieldPropsUnion } from "../elements/types";
import { CostCalculationPercentageField, CostCalculationPercentageInputTypeKey } from "./CostCalculationPercentageField";
import { DatePickerInternal, FileUploadInternal, HighlightsFieldInternal, IntegerFieldInternal, KeyUndefinedError, SelectFieldInternal, TextFieldInternal } from "./InputField";
import { PropertyField, PropertyInputTypeKey } from "./PropertyField";

type SaleFieldDescriptor = {
  [key in keyof InputFieldPropsUnion]: any;
}

type Predefined =
  | 'id'
  | 'limit'
  | 'startAt'
  | "corePropertyRepresentations"

export type SaleInputTypeKey = keyof Omit<SaleInputTypeUnion, Predefined>;

type SaleInputTypeSubkey =
  | CostCalculationPercentageInputTypeKey
  | PropertyInputTypeKey

type SaleInputTypeIndexkey =
  | CostCalculationPercentageType

type SaleFieldProps = SubformProps<SaleTypeIntersection> & Partial<SaleFieldDescriptor> & {
  id: SaleInputTypeKey;
  index?: number;
  indexkey?: SaleInputTypeIndexkey;
  noLabels?: boolean;
  onFilesSelected?: (value: Array<AcceptedFileType>) => void;
  onUploadEnded?: (value: Array<AcceptedFileType>) => void;
  subkey?: SaleInputTypeSubkey;
}


export const getPlaceholder = (
  id: string, noLabels: boolean
) => {
  if (noLabels) {
    return undefined;
  }

  switch (id) {
    case "assetType":
      return i18n.t("Select...");
    case "baFinId":
      return i18n.t("BaFinID");
    case "company":
      return i18n.t("Company");
    case "description":
      return i18n.t("Description");
    case "descriptionTitle":
      return i18n.t("Description Title");
    case "fee":
      return i18n.t("Fee");
    case "feeEnd":
      return i18n.t("Fee End");
    case "feeOngoing":
      return i18n.t("Fee Ongoing");
    case "highlights":
      return i18n.t("Highlight");
    case "interestRate":
      return i18n.t("Interest");
    case "interestPayoutPeriod":
      return i18n.t("Select...");
    case "investmentClass":
      return i18n.t("Select...");
    case "maxPurchase":
      return i18n.t("Maximum Purchase");
    case "minPurchase":
      return i18n.t("Minimum Purchase");
    case "title":
      return i18n.t("Title");
    case "estimatedValueIncrease":
      return i18n.t("Estimated value increase");
    case "predictedPerformance":
      return i18n.t("Predicted performance");
    case "scoreCustomerGroup":
      return i18n.t("Customer score");
    case "scoreInvestmentClass":
      return i18n.t("Investment class score");
    case "scoreWPServices":
      return i18n.t("WP services score");
    case "platformAssetValue":
      return i18n.t("Platform asset value");
    case "subtitle":
      return i18n.t("Subtitle");
    case "supertitle":
      return i18n.t("Supertitle");
    case "totalAssetValue":
      return i18n.t("Total asset value");
    case "documents":
      return i18n.t("documents uploaded");
    case "images":
      return i18n.t("images uploaded");
    default:
      return undefined;
  }
}

export const getLabel = (
  id: string, noLabels: boolean
) => {
  if (noLabels) {
    return undefined;
  }

  switch (id) {
    case "assetType":
      return i18n.t("Asset type");
    case "endDate":
      return i18n.t("Sale end");
    case "firstPayoutOfInterest":
      return i18n.t("First payout of interest");
    case "interestPayoutPeriod":
      return i18n.t("Interest payout period");
    case "investmentClass":
      return i18n.t("Investment class");
    case "startDate":
      return i18n.t("Sale start");
    case "documents":
      return i18n.t("Upload documents");
    case "images":
      return i18n.t("Upload images");
    default:
      return undefined;
  }
}

export const SaleField = (props: SaleFieldProps) : JSX.Element | null => {
  // Subform Props

  const {
    errors,
    index,
    indexkey,
    setFieldValue,
    subkey,
    touched,
    values,
  } = props;

  // Generated Props

  const id = props.id as SaleInputTypeKey;

  // Hooks

  const { t } = useTranslation();

  // Building Component

  switch (id) {
    case "assetType": {
      return (
        <SelectFieldInternal
          label={t("Asset type")}
          placeholder={t("Select...")}
          options={SelectOptions().asset}
          { ...props }
        />
      );
    }
    case "baFinId": {
      const text = t("BaFinID");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
        />
      );
    }
    case "company": {
      const text = t("Company");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
        />
      );
    }
    case "description": {
      const text = t("Description");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
        />
      );
    }
    case "descriptionTitle": {
      const text = t("Description Title");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
        />
      );
    }
    case "documents": {
      return (
        <FileUploadInternal
          buttonValue={t("Upload documents")}
          stateValue={t("documents uploaded")}
          startIcon={<AddIcon />}
          {...props}
        />
      );
    }
    case "estimatedValueIncrease": {
      const text = t("Estimated value increase");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{
            min: CreateSaleManageable.minPercentage,
            max: CreateSaleManageable.maxPercentage,
          }}
          type="number"
          unit="%"
        />
      );
    }
	  case "endDate": {
      let minDate = undefined;
      if ('startDate' in values && values.startDate) {
        minDate = new Date(values.startDate);
        minDate.setDate(minDate.getDate() + 1);
      }

      return (
        <DatePickerInternal
          label={t("Sale end")}
          minDate={minDate}
          { ...props }
          disablePast
        />
      );
    }
    case "fee": {
      const text = t("Fee");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minFee }}
          type="number"
          unit="%"
        />
      );
    }
    case "feeEnd": {
      const text = t("Fee End");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minFee }}
          type="number"
          unit="%"
        />
      );
    }
    case "feeOngoing": {
      const text = t("Fee Ongoing");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minFee }}
          type="number"
          unit="%"
        />
      );
    }
	  case "firstPayoutOfInterest": {
      let minDate = undefined;
      if ('startDate' in values && values.startDate) {
        minDate = new Date(values.startDate);
        minDate.setDate(minDate.getDate() + 1);
      }

      return (
        <DatePickerInternal
          label={t("First payout of interest")}
          minDate={minDate}
          { ...props }
          disablePast
        />
      );
    }
    case "highlights": {
      return (
        <HighlightsFieldInternal
          {...props}
          placeholder={t("Highlight")}
        />
      )
    }
    case "interestPayoutPeriod": {
      return (
        <SelectFieldInternal
          label={t("Interest payout period")}
          placeholder={t("Select...")}
          options={SelectOptions().period}
          { ...props }
        />
      );
    }
    case "interestRate": {
      const text = t("Interest");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minInterestRate }}
          type="number"
          unit="%"
        />
      );
    }
    case "investmentClass": {
      return (
        <SelectFieldInternal
          label={t("Investment class")}
          placeholder={t("Select...")}
          options={SelectOptions().investmentClass}
          { ...props }
          disabled
        />
      );
    }
    case "images": {
      return (
        <FileUploadInternal
          buttonValue={t("Upload images")}
          stateValue={t("images uploaded")}
          startIcon={<CameraAlt />}
          {...props}
        />
      );
    }
    case "maxPurchase": {
      const text = t("Maximum Purchase");
      return (
        <IntegerFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minMaxPurchase }}
          unit="€"
        />
      );
    }
    case "minPurchase": {
      const text = t("Minimum Purchase");
      return (
        <IntegerFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minMinPurchase }}
          unit="€"
        />
      );
    }
    case "percentages": {
      if ('percentages' in values && indexkey !== undefined && subkey !== undefined) {
        const index = values.percentages ? Array.from(values.percentages).findIndex((percentage: any) => percentage.type === indexkey) : -1;
        return (
          <CostCalculationPercentageField
            {...props}
            errors={errors.percentages?.[index] || {}}
            id={subkey as CostCalculationPercentageInputTypeKey}
            setFieldValue={(
              id: string, value: any
            ) => {
              let field = values.percentages ? [...values.percentages] : [];

              if (index < 0) {
                field.push(new CostCalculationPercentage());
                field[field.length - 1].type = indexkey;
                field[field.length - 1][id] = value;
              } else {
                field[index][id] = value;
              }

              setFieldValue(
                'percentages',
                field
              );
            }}
            setValues={(values: any) => setFieldValue(
              'percentages',
              values
            )}
            touched={touched.percentages?.[index] || {}}
            values={values.percentages?.[index] || new CostCalculationPercentage()}
          />
        );
      } else {
        throw new Error('Accessing percentages but no percentages found');
      }
    }
    case "platformAssetValue": {
      const text = t("Platform asset value");
      return (
        <IntegerFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minAssetValue }}
          unit="€"
        />
      );
    }
    case "predictedPerformance": {
      const text = t("Predicted performance");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minPercentage }}
          type="number"
          unit="%"
        />
      );
    }
    case "properties": {
      if ('properties' in values && index !== undefined) {
        return (
          <PropertyField
            {...props}
            errors={errors.properties?.[index] || {}}
            id={subkey as PropertyInputTypeKey}
            setFieldValue={(
              id: string, value: any
            ) => {
              let field = values.properties ? [...values.properties] : [];

              if (index < 0) {
                field.push(new Property());
                field[field.length - 1].type = PropertyType.RICH_TEXT;
                field[field.length - 1][id] = value;
              } else {
                field[index][id] = value;
              }

              setFieldValue(
                'properties',
                field
              );
            }}
            setValues={(values: any) => setFieldValue(
              'properties',
              values
            )}
            touched={touched.properties?.[index] || {}}
            values={values.properties?.[index] || new Property()}
          />
        );
      } else {
        throw new Error('Accessing properties but no properties found');
      }
    }
    case "scoreCustomerGroup": {
      const text = t("Customer score");
      return (
        <IntegerFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{
            min: CreateSaleManageable.minScoreCustomerGroup,
            max: CreateSaleManageable.maxScoreCustomerGroup,
          }}
        />
      );
    }
    case "scoreInvestmentClass": {
      const text = t("Investment class score");
      return (
        <IntegerFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{
            min: CreateSaleManageable.minScoreInvestmentClass,
            max: 'investmentClass' in values && values.investmentClass ? maxScoreInvestmentClass(values.investmentClass) : undefined,
          }}
        />
      );
    }
    case "scoreWPServices": {
      const text = t("WP services score");
      return (
        <IntegerFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{
            min: CreateSaleManageable.minScoreWPServices,
            max: CreateSaleManageable.maxScoreWPServices,
          }}
        />
      );
    }
	  case "startDate": {
      let maxDate = undefined;
      if ('endDate' in values && values.endDate) {
        maxDate = new Date(values.endDate);
        maxDate.setDate(maxDate.getDate() - 1);
      }

      return (
        <DatePickerInternal
          label={t("Sale start")}
          maxDate={maxDate}
          { ...props }
          disablePast
        />
      );
    }
    case "subtitle": {
      const text = t("Subtitle");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
        />
      );
    }
    case "supertitle": {
      const text = t("Supertitle");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
        />
      );
    }
    case "title": {
      const text = t("Title");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          { ...props }
        />
      );
    }
    case "totalAssetValue": {
      const text = t("Total asset value");
      return (
        <IntegerFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{ min: CreateSaleManageable.minAssetValue }}
          unit="€"
        />
      );
    }
    default: {
      KeyUndefinedError(id); // Compile-time check for unhandled cases
    }
  }

  return <></>;
}
